import { useCallback, useMemo } from 'react';
import { Folder } from 'react-feather';
import type { TreeViewProps, TreeItemContextValue } from '@trinity/chat-components/components/TreeView';
import { TreeView, TreeItem } from '@trinity/chat-components/components/TreeView';
import { cx, trunc, getAllFolderDestinations, type QuantFolderTreeItem } from '@trinity/chat-components/utils';
import styles from './style/QuantFolderTreeView.module.css';

type Props = {
  data: QuantFolderTreeItem;
  selectedFolderId?: string | null;
} & Omit<TreeViewProps, 'children'>;

export const QuantFolderTreeView = ({ data, selectedFolderId, ...treeProps }: Props) => {

  // Helper function to find all parent folder IDs that need to be expanded
  const getExpandedNodes = useCallback((targetId: string | null): number[] => {
    if (!targetId) return [+data.id];

    const allFolders = getAllFolderDestinations(data);
    const targetFolder = allFolders.find(folder => folder.id === targetId);

    if (!targetFolder) return [+data.id];

    // Build path to target by walking up the folder structure
    const pathParts = targetFolder.path.split('/').filter(part => part);
    const expandedIds = [+data.id]; // Always include root

    // Find each folder in the path and add its ID
    let currentPath = '';
    for (const part of pathParts) {
      currentPath = currentPath ? `${currentPath}/${part}` : part;
      const folderInPath = allFolders.find(folder =>
        folder.path === `/${currentPath}` || folder.name === part,
      );
      if (folderInPath && folderInPath.id !== null) {
        expandedIds.push(+folderInPath.id);
      }
    }

    return expandedIds;
  }, [data]);

  // Calculate which nodes should be expanded based on selection
  const defaultExpandedNodes = useMemo(() => {
    const baseExpanded = [+data.id];
    if (!selectedFolderId) return baseExpanded;

    const pathToSelected = getExpandedNodes(selectedFolderId);
    return pathToSelected.length > 1 ? pathToSelected : baseExpanded;
  }, [data, selectedFolderId, getExpandedNodes]);

  // Convert selected folder ID to number for TreeView
  const defaultSelectedNode = useMemo(() => {
    return selectedFolderId ? +selectedFolderId : undefined;
  }, [selectedFolderId]);

  const renderNode = useCallback((folder: QuantFolderTreeItem) => (ctx: TreeItemContextValue) => {

    const rootClass = cx(styles.node, {
      [styles.expandable]: ctx.expandable,
      [styles.selected]: ctx.selected,
    });

    const expandClass = cx({
      [styles.chevronUp]: ctx.expandable && !ctx.expanded,
      [styles.chevronDown]: ctx.expandable && ctx.expanded,
      [styles.empty]: !ctx.expandable,
    });

    return (
      <div className={rootClass}>
        <div className={expandClass} />
        <div className={styles.label}>
          <Folder className={styles.icon} />
          <div className={styles.labelText}>
            {trunc(folder.name, 50)}
          </div>
        </div>
      </div>
    );
  }, []);

  const renderTree = useCallback((item: QuantFolderTreeItem) => (
    <TreeItem
      key={item.id}
      classes={{ children: styles.children }}
      nodeId={Number(item.id)}
      renderNode={renderNode(item)}>
      {item.children.map(node => renderTree(node))}
    </TreeItem>
  ), [renderNode]);

  return (
    <TreeView
      key={`tree-${selectedFolderId || 'root'}`}
      defaultExpanded={defaultExpandedNodes}
      defaultSelected={defaultSelectedNode}
      {...treeProps}>
      {renderTree(data)}
    </TreeView>
  );
};

export default QuantFolderTreeView;
